"use client";

import type { ConsentPreferences } from "@/lib/consent/types";

const TRACKING_CONSENT_STORAGE_KEY = "deepface_tracking_consent_v1";
const CONSENT_PROMPT_STORAGE_KEY = "deepface_consent_prompt_seen_v1";
export const CONSENT_UPDATED_EVENT = "deepface:consent-updated";

type TrackingConsentSnapshot = {
  analytics_tracking: boolean;
  session_replay: boolean;
  saved_at: string;
};

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function readTrackingConsentSnapshot(): TrackingConsentSnapshot | null {
  if (!canUseStorage()) return null;

  const raw = window.localStorage.getItem(TRACKING_CONSENT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<TrackingConsentSnapshot>;
    if (
      typeof parsed.analytics_tracking !== "boolean" ||
      typeof parsed.session_replay !== "boolean"
    ) {
      return null;
    }
    return {
      analytics_tracking: parsed.analytics_tracking,
      session_replay: parsed.session_replay,
      saved_at:
        typeof parsed.saved_at === "string" && parsed.saved_at
          ? parsed.saved_at
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function writeTrackingConsentSnapshot(preferences: ConsentPreferences) {
  if (!canUseStorage()) return;

  const snapshot: TrackingConsentSnapshot = {
    analytics_tracking: Boolean(preferences.analytics_tracking),
    session_replay: Boolean(preferences.session_replay),
    saved_at: new Date().toISOString(),
  };
  window.localStorage.setItem(
    TRACKING_CONSENT_STORAGE_KEY,
    JSON.stringify(snapshot)
  );
}

export function clearTrackingConsentSnapshot() {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(TRACKING_CONSENT_STORAGE_KEY);
}

export function hasSeenConsentPrompt() {
  if (!canUseStorage()) return false;
  return window.localStorage.getItem(CONSENT_PROMPT_STORAGE_KEY) === "1";
}

export function markConsentPromptSeen() {
  if (!canUseStorage()) return;
  window.localStorage.setItem(CONSENT_PROMPT_STORAGE_KEY, "1");
}

export function clearConsentPromptSeen() {
  if (!canUseStorage()) return;
  window.localStorage.removeItem(CONSENT_PROMPT_STORAGE_KEY);
}
